import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import i18n from '../../i18n';
// Chart option objects are built in plain helper functions and default
// parameters, where React hooks cannot be called — so translate through the
// i18n instance directly. Charts re-render when their page re-renders on a
// language change, so labels stay in sync.
const t = (key, opts) => i18n.t(key, opts);

const AvgWeightGanttChart = ({ data, title = null }) => {
  // default resolved in-body: the hook is not in scope in the parameter list
  const chartTitle = title ?? t('charts:titles.gantt');
  // FCR-based gradient color with smooth transitions
  // Creates a gradient from green (good) to red (poor) based on FCR value
  const getFCRGradientColor = (fcr) => {
    const fcrValue = parseFloat(fcr);

    // Define gradient stops based on FCR ranges
    // Good range (< 1.6): Green shades
    if (fcrValue <= 1.3) {
      return {
        type: 'linear',
        x: 0, y: 0, x2: 1, y2: 0,
        colorStops: [
          { offset: 0, color: '#1e8449' },
          { offset: 1, color: '#27ae60' }
        ]
      };
    }
    if (fcrValue < 1.6) {
      return {
        type: 'linear',
        x: 0, y: 0, x2: 1, y2: 0,
        colorStops: [
          { offset: 0, color: '#27ae60' },
          { offset: 1, color: '#52c41a' }
        ]
      };
    }

    // Average range (1.6 - 1.8): Orange shades
    if (fcrValue < 1.7) {
      return {
        type: 'linear',
        x: 0, y: 0, x2: 1, y2: 0,
        colorStops: [
          { offset: 0, color: '#f39c12' },
          { offset: 1, color: '#faad14' }
        ]
      };
    }
    if (fcrValue < 1.8) {
      return {
        type: 'linear',
        x: 0, y: 0, x2: 1, y2: 0,
        colorStops: [
          { offset: 0, color: '#faad14' },
          { offset: 1, color: '#ff9800' }
        ]
      };
    }

    // Poor range (> 1.8): Red shades
    if (fcrValue < 2.0) {
      return {
        type: 'linear',
        x: 0, y: 0, x2: 1, y2: 0,
        colorStops: [
          { offset: 0, color: '#ff5722' },
          { offset: 1, color: '#e74c3c' }
        ]
      };
    }

    // Very poor (> 2.0): Dark red gradient
    return {
      type: 'linear',
      x: 0, y: 0, x2: 1, y2: 0,
      colorStops: [
        { offset: 0, color: '#e74c3c' },
        { offset: 1, color: '#c0392b' }
      ]
    };
  };

  const processedData = useMemo(() => {
    if (!data || data.length === 0) return null;

    // Sort by batchID descending for consistent display
    const sorted = [...data].sort((a, b) => b.batchID - a.batchID);

    return {
      yAxisLabels: sorted.map(b =>
        b.farmName ? `${b.farmName} — ${b.batchName}` : b.batchID.toString()
      ),
      series: sorted.map(batch => ({
        value: parseInt(batch.duration),
        itemStyle: {
          color: getFCRGradientColor(batch.fcr),
          borderRadius: [0, 4, 4, 0] // Rounded right edge
        },
        label: {
          show: true,
          position: 'insideLeft',
          formatter: batch.batchName,
          fontSize: 12,
          color: '#fff',
          fontWeight: 600,
          padding: [0, 0, 0, 10]
        },
        // Store data for tooltip
        batchName: batch.batchName,
        batchID: batch.batchID,
        farmName: batch.farmName,
        startDate: batch.startDate,
        fcr: batch.fcr,
        avgBirdWeight: batch.avgBirdWeight,
        livability: batch.livability,
        pef: batch.pef,
        initialChicks: batch.initialChicks,
        liveBirds: batch.liveBirds,
        metrics: `${batch.avgBirdWeight} kg | ${batch.duration} days | ${t('charts:tooltip.fcr')}: ${batch.fcr}`
      }))
    };
  }, [data]);

  const option = useMemo(() => {
    return {
      title: {
        text: chartTitle,
        left: 'center',
        top: 20,
        textStyle: { fontSize: 16, fontWeight: 'bold', color: '#333' }
      },
      tooltip: {
        trigger: 'item',
        confine: true,
        borderWidth: 0,
        backgroundColor: 'rgba(30,30,30,0.9)',
        textStyle: { color: '#fff' },
        formatter: (params) => {
          const d = params.data;
          return `
            <div style="padding:8px;">
              <div style="font-weight:700;margin-bottom:6px;font-size:14px;">${d.batchName}</div>
              <div style="margin-bottom:2px;">${t('charts:tooltip.farm')}: ${d.farmName ?? '-'}</div>
              <div style="margin-bottom:2px;">${t('charts:tooltip.batchId')}: ${d.batchID ?? params.name}</div>
              <div style="margin-bottom:2px;">${t('charts:tooltip.startDate')}: ${d.startDate}</div>
              <div style="margin-bottom:2px;">${t('charts:tooltip.duration')}: <b>${params.value} ${t('charts:tooltip.durationDays')}</b></div>
              <div style="margin-bottom:2px;">${t('charts:tooltip.avgWeight')}: ${d.avgBirdWeight} kg</div>
              <div style="color:${params.color};font-weight:bold;margin-bottom:2px;font-size:13px;">${t('charts:tooltip.fcr')}: ${d.fcr}</div>
              <div style="margin-bottom:2px;">${t('charts:tooltip.livability')}: ${d.livability}%</div>
              <div style="margin-bottom:2px;">${t('charts:tooltip.pef')}: ${d.pef}</div>
              <div style="margin-top:4px;padding-top:4px;border-top:1px solid rgba(255,255,255,0.2);">
                <div>${t('charts:tooltip.initial')}: ${d.initialChicks} chicks</div>
                <div>${t('charts:tooltip.liveBirds')}: ${d.liveBirds}</div>
              </div>
            </div>
          `;
        }
      },
      grid: {
        left: 60,
        right: 220,  // Increased space for FCR gradient legend and labels
        top: 80,
        bottom: 60,
        containLabel: true
      },
      xAxis: {
        type: 'value',
        name: t('charts:axis.durationDays'),
        nameLocation: 'middle',
        nameGap: 35,
        nameTextStyle: { fontSize: 14, fontWeight: 'bold', color: '#666' },
        min: 0,
        max: 50,
        splitLine: { show: true, lineStyle: { type: 'dashed', color: '#e0e0e0' } },
        axisLine: { lineStyle: { color: '#ccc' } },
        axisTick: { lineStyle: { color: '#ccc' } },
        axisLabel: { color: '#666', fontSize: 11 }
      },
      yAxis: {
        type: 'category',
        name: t('charts:common.batch'),
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: { fontSize: 14, fontWeight: 'bold', color: '#666' },
        data: processedData?.yAxisLabels || [],
        axisLabel: { fontSize: 12, color: '#333', fontWeight: 500, width: 140, overflow: 'truncate' },
        axisLine: { lineStyle: { color: '#ccc' } },
        axisTick: { lineStyle: { color: '#ccc' } }
      },
      series: [{
        type: 'bar',
        barWidth: '65%',
        data: processedData?.series || [],
        animationDuration: 800,
        animationEasing: 'cubicOut',
        // Add right-side label for metrics
        labelLayout: {
          hideOverlap: false
        },
        endLabel: {
          show: true,
          formatter: (params) => params.data.metrics,
          fontSize: 11,
          color: '#333',
          padding: [2, 8],
          backgroundColor: 'rgba(255,255,255,0.85)',
          borderRadius: 3
        }
      }],
      // Visual Map for FCR Gradient Legend (Right side)
      visualMap: {
        type: 'continuous',
        min: 1.2,
        max: 2.2,
        right: 20,
        top: 100,
        calculable: false,
        text: ['2.2', '1.2'],
        textStyle: {
          color: '#333',
          fontSize: 12,
          fontWeight: 500
        },
        inRange: {
          color: ['#1e8449', '#27ae60', '#52c41a', '#f39c12', '#faad14', '#ff9800', '#ff5722', '#e74c3c', '#c0392b']
        },
        formatter: (value) => {
          return value.toFixed(1);
        },
        itemWidth: 25,
        itemHeight: 240,
        textGap: 10,
        orient: 'vertical',
        show: true,
        // Add performance labels
        splitNumber: 5,
        pieces: [
          { min: 1.8, max: 2.2, label: t('charts:common.poor'), color: '#e74c3c' },
          { min: 1.6, max: 1.8, label: t('charts:common.average'), color: '#faad14' },
          { min: 1.2, max: 1.6, label: t('charts:common.good'), color: '#27ae60' }
        ]
      },
      // Add "FCR" label above the gradient bar
      graphic: [
        {
          type: 'text',
          right: 35,
          top: 80,
          style: {
            text: 'FCR',
            font: 'bold 14px sans-serif',
            fill: '#333'
          }
        }
      ]
    };
  }, [processedData, title]);

  // No data placeholder
  if (!data || data.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "500px",
          backgroundColor: "#f8f9fa",
          borderRadius: "6px",
          border: "1px solid #dee2e6",
        }}
      >
        <div style={{ textAlign: "center", maxWidth: "300px" }}>
          <div style={{ fontSize: "48px", marginBottom: "15px", color: "#dee2e6" }}>📅</div>
          <div style={{ fontSize: "18px", fontWeight: 600, marginBottom: 8, color: "#495057" }}>
            {t('charts:common.noDataAvailable')}
          </div>
          <div style={{ fontSize: 14, color: "#6c757d", lineHeight: 1.4 }}>
            {t('charts:common.applyFiltersGantt')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="Temp">
      <ReactECharts
        option={option}
        style={{ width: "100%", height: "500px" }}
        opts={{ renderer: "svg" }}
      />
    </div>
  );
};

export default AvgWeightGanttChart;
